import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Subscription } from 'rxjs';
import { SettlementMainService } from './settlement-main.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-settlement-main',
    templateUrl: './settlement-main.component.html',
    styleUrls: ['./settlement-main.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SettlementMainComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  // Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  // Standard component properties
  minimise = false;
  loading = false;
  crudLoading = false;
  disable = true;

  // Data properties
  modalData: any[] = [];
  tableData: any[] = [];
  federalData: any[] = [];
  stateData: any[] = [];
  finalData: any[] = [];
  typesData: any[] = [];
  adnBucCodes: any[] = [];
  federalCodes: any[] = [];
  stateCodes: any[] = [];

  // Row data
  rowData: any = {};
  remFederalAmt: any = 0;
  remStateAmt: any = 0;

  // New row state
  newRow = { federal: false, state: false };
  amount = 0;
  amount2 = 0;
  transKey = 0;

  // User/config
  logged_in_user = '';
  globalParams: any = {};
  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private settlementMainService: SettlementMainService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.globalParams = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.remFederalAmt = this.rowData['Federal_Method_of_Settlement_Remaining'] || 0;
    this.remStateAmt = this.rowData['State_Method_of_Settlement_Remaining'] || 0;
    this.cdr.detectChanges();
  }

  // Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    if (!this.GENERAL_CONFIG.base_url) {
      return;
    }

    this.isInitialized = true;
    this.getModalData();
  }

  getModalData() {
    this.loading = true;
    this.isLoading = true;
    this.federalData = [];
    this.stateData = [];
    this.federalCodes = [];
    this.stateCodes = [];
    this.cdr.detectChanges();

    this.settlementMainService.fetchModalData(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.globalParams,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.tableData = response.jsonObject || [];
        this.tableData.forEach((value: any) => {
          if (value.LINE_DESCRIPTION === 'Federal Method of Settlement - ADN Allocated') {
            this.federalData.push({ ...value, IS_EDIT_MODE: 'N' });
          }
          if (value.LINE_DESCRIPTION === 'State Method of Settlement - ADN Allocated') {
            this.stateData.push({ ...value, IS_EDIT_MODE: 'N' });
          }
        });

        // Fetch ADN/BUC codes and settlement types in parallel
        forkJoin([
          this.settlementMainService.getAdnBucCodes(
            this.GENERAL_CONFIG.base_url,
            this.logged_in_user,
            this.filterParams
          ),
          this.settlementMainService.getSettlementTypes(
            this.GENERAL_CONFIG.base_url,
            this.logged_in_user,
            this.filterParams
          )
        ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          ([adnBucResponse, typesResponse]: any[]) => {
            this.loading = false;
            this.isLoading = false;
            this.adnBucCodes = adnBucResponse.jsonObject || [];
            this.typesData = typesResponse.jsonObject || [];

            console.log('Settlement Main - ADN/BUC Codes loaded:', this.adnBucCodes.length);
            console.log('Settlement Main - Settlement Types loaded:', this.typesData.length);

            this.adnBucCodes.forEach((value: any) => {
              if (value.SETT_METHOD && (value.SETT_METHOD.toUpperCase() === 'ALL' || value.SETT_METHOD.toUpperCase() === 'FEDERAL')) {
                this.federalCodes.push(value);
              }
              if (value.SETT_METHOD && (value.SETT_METHOD.toUpperCase() === 'ALL' || value.SETT_METHOD.toUpperCase() === 'STATE')) {
                this.stateCodes.push(value);
              }
            });

            console.log('Settlement Main - Federal Codes:', this.federalCodes.length);
            console.log('Settlement Main - State Codes:', this.stateCodes.length);
            this.cdr.detectChanges();
          },
          (error: any) => {
            this.loading = false;
            this.isLoading = false;
            console.error('Error loading ADN/BUC codes or settlement types:', error);
            this._snackBar.open('Error loading dropdown data. Please refresh and try again.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.cdr.detectChanges();
          }
        );
      },
      (error: any) => {
        this.loading = false;
        this.isLoading = false;
        console.error('Error loading modal data:', error);
        this._snackBar.open('Error loading settlement data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  AddNewRow(type: string) {
    this.disable = false;
    if (type === 'Federal') {
      this.federalData.push({
        FORM_NAME: 'F_SETTLEMENT',
        METHOD_OF_SETTLEMENT: 'Federal Method of Settlement',
        LINE_DESCRIPTION: 'Federal Method of Settlement - Allocated',
        IS_CHANGED_FLAG: 'I',
        SETTLEMENT_TYPE: (this.typesData && this.typesData.length) ? this.typesData[0].LABEL : '',
        BUC_CODE: (this.federalCodes && this.federalCodes.length) ? this.federalCodes[0].BUC_CODE : '',
        ADN_CODE: (this.federalCodes && this.federalCodes.length) ? this.federalCodes[0].ADN_CODE : '',
        AMOUNT: 0,
        OCCURRENCE: this.federalData.length + 1,
        ADN_BUC_CODE_KEY: (this.federalCodes && this.federalCodes.length) ? this.federalCodes[0].ADN_BUC_CODE_KEY : '',
        COMBINATION_KEY: this.rowData.COMBINATION_KEY,
        ATTRIB_NAME: 'SETTLE_FED_METHOD_ALLOC_ADN',
        TRANS_DETAILS_KEY: this.transKey > 0 ? this.transKey : 0,
        IS_EDIT_MODE: 'Y',
        OTHER_DESC: ''
      });
      this.amount = 0;
      this.newRow.federal = true;
    } else {
      this.stateData.push({
        FORM_NAME: 'F_SETTLEMENT',
        METHOD_OF_SETTLEMENT: 'State Method of Settlement',
        LINE_DESCRIPTION: 'State Method of Settlement - Allocated',
        IS_CHANGED_FLAG: 'I',
        SETTLEMENT_TYPE: (this.typesData && this.typesData.length) ? this.typesData[0].LABEL : '',
        BUC_CODE: (this.stateCodes && this.stateCodes.length) ? this.stateCodes[0].BUC_CODE : '',
        ADN_CODE: (this.stateCodes && this.stateCodes.length) ? this.stateCodes[0].ADN_CODE : '',
        AMOUNT: 0,
        OCCURRENCE: this.stateData.length + 1,
        ADN_BUC_CODE_KEY: (this.stateCodes && this.stateCodes.length) ? this.stateCodes[0].ADN_BUC_CODE_KEY : '',
        COMBINATION_KEY: this.rowData.COMBINATION_KEY,
        ATTRIB_NAME: 'SETTLE_STATE_METHOD_ALLOC_ADN',
        TRANS_DETAILS_KEY: this.transKey > 0 ? this.transKey : 0,
        IS_EDIT_MODE: 'Y',
        OTHER_DESC: ''
      });
      this.amount2 = 0;
      this.newRow.state = true;
    }
    this.cdr.detectChanges();
  }

  add(type: string, item: any) {
    if (type === 'Federal') {
      item.AMOUNT = parseFloat(item.AMOUNT) + parseFloat(this.remFederalAmt);
    } else {
      item.AMOUNT = parseFloat(item.AMOUNT) + parseFloat(this.remStateAmt);
    }
    this.cdr.detectChanges();
  }

  overwrite(type: string, item: any) {
    if (type === 'Federal') {
      item.AMOUNT = this.remFederalAmt;
    } else {
      item.AMOUNT = this.remStateAmt;
    }
    this.cdr.detectChanges();
  }

  cancel() {
    this.crudLoading = false;
    console.log('in cancel');
    this.requestGridUpdate.emit('cancel');
  }

  reset() {
    this.isInitialized = false;
    this.isLoading = false;
    this.federalData = [];
    this.stateData = [];
    this.federalCodes = [];
    this.stateCodes = [];
    this.disable = true;
    this.getModalData();
  }

  save() {
    this.crudLoading = true;
    const clobSettings = [{ tax_year: this.globalParams.tax_year || '' }];
    this.finalData = [...this.federalData, ...this.stateData];

    if (this.checkErrors()) {
      this.settlementMainService.saveModal(
        this.GENERAL_CONFIG.base_url,
        this.finalData,
        clobSettings,
        this.globalParams,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.crudLoading = false;
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Save Successful', 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.crudLoading = false;
          console.error('Error saving settlement data:', error);
          this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      );
    } else {
      this.crudLoading = false;
      this.cdr.detectChanges();
    }
  }

  checkErrors(): boolean {
    let error = '';
    this.finalData.forEach(item => {
      if (item.IS_CHANGED_FLAG !== 'D') {
        if (item.SETTLEMENT_TYPE !== 'IBS' && item.OTHER_DESC === '') {
          error += 'Other Code cannot be empty. ';
        }
        if (item.AMOUNT == 0) {
          error += 'Amount cannot be 0. ';
        }
      }
    });
    if (error !== '') {
      this._snackBar.open(error, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return false;
    }
    return true;
  }

  dismiss(type: string) {
    if (type === 'Federal') {
      this.newRow.federal = false;
    } else {
      this.newRow.state = false;
    }
    this.cdr.detectChanges();
  }

  valuechanged(codeType: string, type: string, item: any) {
    if (item.SETTLEMENT_TYPE === 'IBS') {
      if (type === 'Federal') {
        if (codeType === 'buc') {
          this.federalCodes.forEach((value, index) => {
            if (value.BUC_CODE === item.BUC_CODE) {
              item.ADN_CODE = this.federalCodes[index].ADN_CODE;
              item.ADN_BUC_CODE_KEY = this.federalCodes[index].ADN_BUC_CODE_KEY;
            }
          });
        } else {
          this.federalCodes.forEach((value, index) => {
            if (value.ADN_CODE === item.ADN_CODE) {
              item.BUC_CODE = this.federalCodes[index].BUC_CODE;
              item.ADN_BUC_CODE_KEY = this.federalCodes[index].ADN_BUC_CODE_KEY;
            }
          });
        }
      } else {
        if (codeType === 'buc') {
          this.stateCodes.forEach((value, index) => {
            if (value.BUC_CODE === item.BUC_CODE) {
              item.ADN_CODE = this.stateCodes[index].ADN_CODE;
              item.ADN_BUC_CODE_KEY = this.stateCodes[index].ADN_BUC_CODE_KEY;
            }
          });
        } else {
          this.stateCodes.forEach((value, index) => {
            if (value.ADN_CODE === item.ADN_CODE) {
              item.BUC_CODE = this.stateCodes[index].BUC_CODE;
              item.ADN_BUC_CODE_KEY = this.stateCodes[index].ADN_BUC_CODE_KEY;
            }
          });
        }
      }
    }
    this.cdr.detectChanges();
  }

  removeExistingRecord(occurrence: any, type: string) {
    if (type === 'Federal') {
      this.federalData.forEach((value, idx) => {
        if (value.OCCURRENCE === occurrence) {
          this.federalData[idx].IS_CHANGED_FLAG = 'D';
        }
      });
    } else if (type === 'State') {
      this.stateData.forEach((value, idx) => {
        if (value.OCCURRENCE === occurrence) {
          this.stateData[idx].IS_CHANGED_FLAG = 'D';
        }
      });
    }
    this.disable = false;
    this.cdr.detectChanges();
  }

  editExistingRecord(item: any, type: string) {
    this.disable = false;
    item.IS_EDIT_MODE = 'Y';
    item.IS_CHANGED_FLAG = this.transKey > 0 ? 'U' : 'I';
    this.cdr.detectChanges();
  }

  saveNewEdittedRecord(type: string, item: any) {
    let error = '';
    if (item.SETTLEMENT_TYPE !== 'IBS' && item.OTHER_DESC === '') {
      error += 'Other Code cannot be empty. ';
    }
    if (item.AMOUNT == 0) {
      error += 'Amount cannot be 0.';
    }

    if (error === '') {
      item.IS_EDIT_MODE = 'N';
      item.IS_CHANGED_FLAG = this.transKey > 0 ? 'U' : 'I';
    } else {
      this._snackBar.open(error, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
    }
    this.cdr.detectChanges();
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
